import React from 'react';
import { motion } from 'framer-motion';
import { resolveAssetUrl } from '../lib/assets';
import { MapPin, Phone, Mail, Globe, Facebook, Instagram, Clock, Sparkles, Tag, Navigation } from 'lucide-react';
import BusinessExtras from '../components/BusinessExtras';
import BusinessGiftCards from '../components/business/BusinessGiftCards';
import ServiceBooking from '../components/business/ServiceBooking';
import ContactBusinessButton from '../components/ContactBusinessButton';
import { pluginsFor, orderedSections, pluginEnabled } from '../data/businessCatalog';
import { pageVars } from '../data/motifs';

export default function BusinessCardPage({ data, userId, pageId }) {
  const bc = data?.business_card || {};
  const effPageId = pageId || data?.id || undefined;
  const c = bc.contacts || {};
  const availPlugins = pluginsFor('business_card');
  const on = (id) => availPlugins.includes(id) && pluginEnabled('business_card', bc.plugins, id);
  const secOrder = orderedSections('business_card', bc.module_order);
  const ord = (id) => { const i = secOrder.indexOf(id); return i < 0 ? 99 : i; };

  const pv = pageVars(bc.surface, bc.accent, 'business_card');
  const hasCover = !!bc.cover_url;
  const heroTitle = hasCover ? '#FFFFFF' : 'var(--text-primary)';
  const heroSub = hasCover ? 'rgba(255,255,255,0.78)' : 'var(--text-muted)';

  const contactBtns = [
    c.phone && { icon: Phone, label: 'Zvanīt', href: `tel:${c.phone}`, testid: 'contact-phone' },
    c.email && { icon: Mail, label: 'E-pasts', href: `mailto:${c.email}`, testid: 'contact-email' },
    c.website && { icon: Globe, label: 'Mājaslapa', href: c.website.startsWith('http') ? c.website : `https://${c.website}`, testid: 'contact-website' },
    c.facebook && { icon: Facebook, label: 'Facebook', href: c.facebook.startsWith('http') ? c.facebook : `https://${c.facebook}`, testid: 'contact-facebook' },
    c.instagram && { icon: Instagram, label: 'Instagram', href: c.instagram.startsWith('http') ? c.instagram : `https://${c.instagram}`, testid: 'contact-instagram' },
  ].filter(Boolean);

  const mapHref = bc.lat != null && bc.lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${bc.lat},${bc.lng}`
    : bc.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(bc.address)}` : null;

  return (
    <div className="min-h-screen pb-20" style={{ ...pv, background: 'var(--bg-page)', color: 'var(--text-primary)', fontFamily: 'Manrope, sans-serif' }} data-testid="business-card-page">
      {/* Hero */}
      <div className="relative h-[44vh] min-h-[300px] sm:h-[46vh] sm:min-h-[320px] overflow-hidden">
        {hasCover
          ? <img src={resolveAssetUrl(bc.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 70% 0%, var(--accent-ring), var(--bg-page) 60%)' }} />}
        <div className="absolute inset-0" style={{ background: hasCover
          ? 'linear-gradient(to top, var(--bg-page) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.15) 70%, transparent 100%)'
          : 'linear-gradient(to top, var(--bg-page) 2%, transparent 58%)' }} />
        <div className="relative h-full max-w-4xl mx-auto px-5 flex flex-col justify-end pb-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--accent)] mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {data.owner?.region || 'Latvija'}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-6xl font-light tracking-tighter" style={{ color: heroTitle, fontFamily: 'Outfit, sans-serif' }}>
            {data.owner?.name}
          </motion.h1>
          {bc.tagline && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
            className="text-base sm:text-lg mt-3 max-w-xl" style={{ color: heroSub }}>{bc.tagline}</motion.p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 flex flex-col gap-12 mt-10">
        {/* Contact buttons */}
        <section className="flex flex-wrap gap-3" data-testid="contact-buttons" style={{ order: -1 }}>
          <ContactBusinessButton ownerId={userId} ownerName={data.owner?.name} />
          {contactBtns.map((b) => (
            <a key={b.testid} href={b.href} target={b.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] text-sm font-semibold hover:bg-[var(--accent)] hover:border-[var(--accent)] hover:text-[var(--accent-contrast)] transition-colors"
              data-testid={b.testid}>
              <b.icon className="w-4 h-4" /> {b.label}
            </a>
          ))}
        </section>

        {/* Services & payment (unified booking + online/on-site payment) */}
        <ServiceBooking userId={userId} pageId={effPageId} services={bc.services || []}
          timeSlots={bc.time_slots || []} allowOnline={bc.allow_online} allowOnsite={bc.allow_onsite}
          pv={pv} order={-0.5} />

        {/* About */}
        {bc.about && (
          <section data-testid="about-section" style={{ order: ord('about') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-4" style={{ fontFamily: 'Outfit' }}>Par mums</h2>
            <p className="text-[var(--text-muted)] leading-relaxed whitespace-pre-line">{bc.about}</p>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ order: ord('hours_map') }}>
          {/* Working hours */}
          {on('hours') && bc.working_hours && (
            <section className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 shadow-sm" data-testid="hours-section">
              <h3 className="text-[var(--text-primary)] font-semibold flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}>
                <Clock className="w-5 h-5 text-[var(--accent)]" /> Darba laiks
              </h3>
              <p className="text-[var(--text-muted)] whitespace-pre-line text-sm leading-relaxed">{bc.working_hours}</p>
            </section>
          )}

          {/* Address / map */}
          {on('map') && (bc.address || mapHref) && (
            <section className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 shadow-sm" data-testid="address-section">
              <h3 className="text-[var(--text-primary)] font-semibold flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}>
                <MapPin className="w-5 h-5 text-[var(--accent)]" /> Atrašanās vieta
              </h3>
              {bc.address && <p className="text-[var(--text-muted)] text-sm mb-4">{bc.address}</p>}
              {mapHref && (
                <a href={mapHref} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold hover:bg-[var(--accent-hover)] transition-colors" data-testid="open-map-btn">
                  <Navigation className="w-4 h-4" /> Atvērt kartē
                </a>
              )}
            </section>
          )}
        </div>

        {/* Promotions */}
        {on('promotions') && bc.promotions?.length > 0 && (
          <section data-testid="promotions-section" style={{ order: ord('promotions') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Akcijas & piedāvājumi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {bc.promotions.map((p, i) => (
                <div key={i} className="rounded-2xl border border-[var(--accent-border)] p-5 shadow-sm" data-testid={`promotion-${i}`}
                  style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }}>
                  <Tag className="w-5 h-5 text-[var(--accent)] mb-2" />
                  <p className="text-[var(--text-primary)] font-semibold">{p.title}</p>
                  {p.description && <p className="text-[var(--text-faint)] text-sm mt-1">{p.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gallery */}
        {on('gallery') && bc.gallery?.length > 0 && (
          <section data-testid="card-gallery" style={{ order: ord('gallery') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Galerija</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {bc.gallery.map((g, i) => (
                <img key={i} src={resolveAssetUrl(g)} alt="" className={`w-full rounded-2xl object-cover ${i % 3 === 0 ? 'h-64 row-span-2' : 'h-32'}`} />
              ))}
            </div>
          </section>
        )}
        {/* Gift cards (optional module, OFF by default) */}
        {on('gift_cards') && (
          <BusinessGiftCards userId={userId} pageId={effPageId} cards={bc.gift_cards || []} order={ord('gift_cards')} pv={pv} />
        )}

        <BusinessExtras template="business_card" config={bc} userId={userId} pageId={effPageId} ord={ord} />
      </div>
    </div>
  );
}
